import { Button, Dialog } from "@narmi/design_system";
import { useLocalization } from "@fluent/react";
import cc from "classcat";

import { useCallback, useMemo } from "react";

import SortableList from "../../../../src/components/dragAndDrop/SortableList";
import createDragAnnouncements from "../../../../src/components/dragAndDrop/createDragAnnouncements";
import useDragAnnouncementLabels from "../../../../src/components/dragAndDrop/useDragAnnouncementLabels";
import styles from "./DashboardCustomization.module.scss";
import { useDashboardCustomizationContext } from "../DashboardCustomizationContext";
import DashboardSectionRow from "./DashboardSectionRow";
import SECTION_LABELS_TO_FLUENT_IDS from "../constants";

const CustomizationDialog = () => {
  const { l10n } = useLocalization();
  const {
    isCustomizeDialogOpen,
    isCustomizeLoading,
    closeDialogHandler,
    submitCustomizationHandler,
    leftOrder,
    rightOrder,
    setLeftOrder,
    setRightOrder,
    isPageReloadRequired,
  } = useDashboardCustomizationContext();

  const dragLabels = useDragAnnouncementLabels();

  const getItemLabel = useCallback(
    (id: string) => {
      const ftlId = SECTION_LABELS_TO_FLUENT_IDS[id];
      if (!ftlId) {
        return dragLabels.itemFallback;
      }
      return l10n.getString(ftlId, null) || dragLabels.itemFallback;
    },
    [l10n, dragLabels]
  );

  const announcements = useMemo(
    () => createDragAnnouncements(dragLabels, getItemLabel),
    [dragLabels, getItemLabel]
  );

  const renderRow = useCallback(
    (id: string) => <DashboardSectionRow key={id} id={id} />,
    []
  );

  const submitHandler = useCallback(
    () => submitCustomizationHandler(isPageReloadRequired),
    [submitCustomizationHandler, isPageReloadRequired]
  );

  const footerButtons = useMemo(
    () => (
      <div className={styles["button-container"]}>
        <Button
          startIcon="x"
          kind="tertiary"
          label={l10n.getString("button-close", null)}
          onClick={closeDialogHandler}
        />
        <Button
          kind="primary"
          label={l10n.getString("button-confirm", null)}
          onClick={submitHandler}
          isLoading={isCustomizeLoading}
        />
      </div>
    ),
    [closeDialogHandler, submitHandler, isCustomizeLoading, l10n]
  );

  return (
    <Dialog
      width="776px"
      isOpen={isCustomizeDialogOpen}
      title={l10n.getString("dashboard-customization-title", null)}
      footer={footerButtons}
      onUserDismiss={closeDialogHandler}
    >
      <div className={styles["description-text"]}>
        {l10n.getString("dashboard-customization-description", null)}
      </div>

      <div className={styles["drag-container"]}>
        <div className={cc([styles["drag-section"], styles["left-column"]])}>
          <SortableList
            items={leftOrder}
            onReorder={setLeftOrder}
            renderItem={renderRow}
            announcements={announcements}
          />
        </div>
        <div className={cc([styles["drag-section"], styles["right-column"]])}>
          <SortableList
            items={rightOrder}
            onReorder={setRightOrder}
            renderItem={renderRow}
            announcements={announcements}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default CustomizationDialog;
